import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import api from '../api/client';
import logo from '../logo.png';
import FitToScreen from '../components/FitToScreen';
import { buildTimetableEntries } from '../utils/timetable';

const POLL_INTERVAL = 15000; // ms — client-driven refresh, no reload needed by signage software

const TYPE_STYLE = {
  lan:        { label: 'LAN',      bg: 'bg-sand-500/15',   text: 'text-sand-300',   border: 'border-sand-500/40' },
  tournament: { label: 'Turnier',  bg: 'bg-ocean-500/15',  text: 'text-ocean-300',  border: 'border-ocean-500/40' },
  custom:     { label: 'Programm', bg: 'bg-reef-500/15',   text: 'text-reef-300',   border: 'border-reef-500/40' },
};

function fmtTime(d) {
  return new Date(d).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
}

export default function TimetableShow() {
  const { eventId } = useParams();
  const [event, setEvent] = useState(null);
  const [tournaments, setTournaments] = useState([]);
  const [customEntries, setCustomEntries] = useState([]);
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    let cancelled = false;

    const load = () => {
      Promise.all([
        api.get(`/api/events/${eventId}`),
        api.get(`/api/events/${eventId}/tournaments`),
        api.get(`/api/events/${eventId}/timetable-entries`),
      ]).then(([evRes, tRes, ceRes]) => {
        if (cancelled) return;
        setEvent(evRes.data);
        setTournaments(tRes.data || []);
        setCustomEntries(ceRes.data || []);
      }).catch(() => {});
    };

    load();
    const interval = setInterval(load, POLL_INTERVAL);
    return () => { cancelled = true; clearInterval(interval); };
  }, [eventId]);

  useEffect(() => {
    const clock = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(clock);
  }, []);

  const entries = event ? buildTimetableEntries(event, tournaments, customEntries) : [];
  const nextIdx = entries.findIndex((e) => new Date(e.time).getTime() >= now);

  return (
    <div className="fixed inset-0 bg-ocean-950 flex flex-col overflow-hidden select-none">
      {/* Logo + Titel */}
      <div className="flex items-center flex-shrink-0" style={{ padding: 'clamp(10px, 2.5vh, 32px) clamp(16px, 4vw, 60px)' }}>
        <img src={logo} alt="BeachLAN" className="h-40 w-auto object-contain drop-shadow-lg opacity-90 flex-shrink-0" />
        <div className="flex-1 flex flex-col items-center" style={{ paddingRight: '10rem' }}>
          <h1 className="text-sand-300 font-black uppercase tracking-widest drop-shadow-lg text-center"
            style={{ fontSize: 'clamp(1.2rem, 3.6vh, 3rem)' }}>
            Timetable
          </h1>
          {event?.name && (
            <p className="text-ocean-300 tracking-wide text-center" style={{ fontSize: 'clamp(0.75rem, 1.8vh, 1.3rem)' }}>
              {event.name}
            </p>
          )}
        </div>
      </div>

      {/* Einträge */}
      <div className="flex-1 min-h-0" style={{ padding: '0 clamp(16px, 4vw, 60px) clamp(16px, 3vh, 48px)' }}>
        {entries.length === 0 ? (
          <div className="w-full h-full flex items-center justify-center">
            <p className="text-white/30 text-xl tracking-widest uppercase">Noch keine Termine hinterlegt</p>
          </div>
        ) : (
          <FitToScreen maxScale={2}>
            <div className="flex flex-col" style={{ gap: '14px', width: '58vw', minWidth: 640 }}>
              {entries.map((e, i) => {
                const style = TYPE_STYLE[e.type] || TYPE_STYLE.custom;
                const isPast = new Date(e.time).getTime() < now;
                const isNext = i === nextIdx;
                return (
                  <div
                    key={i}
                    className={`flex items-center gap-5 rounded-2xl border px-7 py-4 transition-colors ${
                      isNext ? 'bg-white/10 border-sand-400 shadow-lg' : `bg-white/[0.03] ${style.border}`
                    } ${isPast && !isNext ? 'opacity-40' : ''}`}
                  >
                    <span className="font-black tabular-nums text-white flex-shrink-0" style={{ fontSize: '1.6rem', minWidth: 150 }}>
                      {fmtTime(e.time)}
                    </span>
                    <span className={`text-[11px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full border ${style.bg} ${style.text} ${style.border} flex-shrink-0`}>
                      {style.label}
                    </span>
                    <span className="font-semibold text-white truncate" style={{ fontSize: '1.5rem' }}>
                      {e.label}
                    </span>
                    {isNext && (
                      <span className="ml-auto text-[11px] font-bold uppercase tracking-widest text-sand-300 flex-shrink-0">
                        ● Als Nächstes
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </FitToScreen>
        )}
      </div>
    </div>
  );
}
